import { AlertOptions, useAppAlert } from '@/app/AppAlertProvider.tsx'
import DocumentPreviewNew from '@/app/common/components/Documents/DocumentPreviewNew'
import { SESSION_STORAGE_KEYS } from '@/app/common/session-storage'
import { MultipleErrorsSchema } from '@/app/common/types_and_interfaces'
import { useWizardContext } from '@/app/pages/family_application/income_verification/IncomeVerificationWizardProvider.tsx'
import { submitDocument } from '@/app/pages/family_application/residency_verification/hooks/useUploadFile.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { MutationStatus } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const ResidencyAlternativeDocsReview = () => {
  const [status, setStatus] = useState<MutationStatus>('idle')
  const { t: translate } = useTranslation()

  const programId = useSelector(selectProgramID)

  const { formData } = useWizardContext()
  const { fireAlert } = useAppAlert()

  const navigateTo = useNavigate()

  const handleSubmitError = (errorMessage: string) => {
    setStatus('error')
    const alertConfig: AlertOptions = {
      variant: 'error',
      message:
        errorMessage ||
        translate('an_error_occurred_whil_processing_the_document'),
      denyBtn: true,
      denyBtnText: translate('retry'),
    }

    fireAlert(alertConfig).then(({ denied }) => {
      if (denied) {
        navigateTo(-1)
      }
    })
  }

  function incremenetNumberOfUploads() {
    const sessionValue = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.numberOfUploadedResidencyDocuments
    )

    if (sessionValue) {
      const uploadsAsNumber = parseInt(sessionValue, 10)
      const newUploads = uploadsAsNumber + 1
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.numberOfUploadedResidencyDocuments,
        newUploads.toString()
      )
    } else {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.numberOfUploadedResidencyDocuments,
        '1'
      )
    }
  }

  const handleSubmitSuccess = () => {
    setStatus('success')
    fireAlert({
      variant: 'success',
      titleText: translate('residency_verify_complete'),
      denyBtn: true,
      denyBtnText: translate('retry'),
      confirmBtnText: translate('confirm'),
    }).then(async ({ confirmed, denied }) => {
      const documentSubmittedPath =
        AppRoutePaths.family_application.residency_verification
          .alternative_docs_verify

      if (confirmed) {
        incremenetNumberOfUploads()
        navigateTo(documentSubmittedPath)
      } else if (denied) {
        navigateTo(documentSubmittedPath)
      }
    })
  }

  
  const onSubmit = async () => {
    if (!formData.DateOfBirthFile) {
      handleSubmitError(translate('no_document_file_found'))
      return
    }

    const numberOfDocuments = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.numberOfUploadedResidencyDocuments
    )
    const isUploadingSecondDocument = numberOfDocuments === '1'
    setStatus('pending')
    const form = new FormData()
    form.append('file', formData.DateOfBirthFile)
    form.append('type', String(1))
    form.append('variation', String(2))
    form.append('program_id', String(programId))
    form.append('referer_page', 'alternative_docs_verify')
    // we want to trigger the flag on the second upload.
    // So we set the document number to 2 when one document is already uploaded to indicate that the second document is about to be uploaded and flag can be set.
    form.append('document_number', isUploadingSecondDocument ? '2' : '1')

    const response = await submitDocument(form)
    const multipleErrors = MultipleErrorsSchema.safeParse(
      response.error?.details?.errorsMessage
    )

    if (response.error) {
      setStatus('error')
      const alertConfig: AlertOptions = {
        variant: 'error',
        title: true,
        titleText: response.error.message,
        messageComponent: (
          <ul className="text-sm space-y-2">
            {multipleErrors.success &&
              multipleErrors.data.map((item) => (
                <li className="list-disc list-inside text-left" key={item}>
                  {item}
                </li>
              ))}
          </ul>
        ),
        message:
          response.error.message ||
          translate('an_error_occurred_whil_processing_the_document'),
        denyBtn: true,
        denyBtnText: translate('retry'),
      }

      fireAlert(alertConfig).then(({ denied }) => {
        if (denied) {
          navigateTo(-1)
        }
      })
    } else {
      await handleSubmitSuccess()
    }
  }

  return (
    <>
      <div className="text-3xl text-center font-medium text-gray-900 mb-10 sm:mb-5">
        {translate('photo_review')}
      </div>

      <div className="flex justify-center">
        <DocumentPreviewNew
          status={status}
          file={formData.DateOfBirthFile}
          onContinueClick={onSubmit}
          onRetakeClick={() => navigateTo(-1)}
        />
      </div>
    </>
  )
}
export default ResidencyAlternativeDocsReview
